/**
 * Videos Hooks
 *
 * TanStack Query hooks for video clip operations.
 * Videos are captured automatically via 24/7 capture scheduler.
 * Handles listing, retry, and deletion.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// Types for video data
export type VideoStatus = 'scheduled' | 'capturing' | 'captured' | 'processing' | 'completed' | 'failed';

export interface VideoClip {
  id: string;
  orgId: string;
  cameraId: string;
  cameraName: string | null;
  sourceNumber: number | null; // Phase 34: 1-based lens (null only in pre-backfill cached responses)
  s3Key: string | null;
  status: VideoStatus;
  scheduledStartTime: string;
  scheduledEndTime: string;
  actualStartTime: string | null;
  actualEndTime: string | null;
  durationSeconds: number | null;
  fileSizeBytes: number | null;
  resolution: string | null;
  frameRate: number | null;
  errorMessage: string | null;
  retryCount: number;
  createdAt: string;
  updatedAt: string;
  downloadUrl?: string | null;
}

export interface VideoListQuery {
  page?: number;
  limit?: number;
  cameraId?: string;
  status?: VideoStatus;
  startDate?: string;
  endDate?: string;
}

export interface PaginatedVideos {
  data: VideoClip[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Query keys
export const videoKeys = {
  all: ['videos'] as const,
  lists: () => [...videoKeys.all, 'list'] as const,
  list: (filters: VideoListQuery) => [...videoKeys.lists(), filters] as const,
  details: () => [...videoKeys.all, 'detail'] as const,
  detail: (id: string) => [...videoKeys.details(), id] as const,
};

/**
 * Fetch video clips list with pagination and filters
 */
export function useVideos(options?: VideoListQuery) {
  const api = useApi();

  return useQuery({
    queryKey: videoKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.page) params.set('page', String(options.page));
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.cameraId) params.set('cameraId', options.cameraId);
      if (options?.status) params.set('status', options.status);
      if (options?.startDate) params.set('startDate', options.startDate);
      if (options?.endDate) params.set('endDate', options.endDate);

      const queryString = params.toString();
      const endpoint = queryString ? `/api/videos?${queryString}` : '/api/videos';

      return api.get<PaginatedVideos>(endpoint);
    },
    refetchInterval: 10000, // Refresh every 10s to update status
  });
}

/**
 * Fetch single video details with download URL
 */
export function useVideo(id: string) {
  const api = useApi();

  return useQuery({
    queryKey: videoKeys.detail(id),
    queryFn: () => api.get<VideoClip>(`/api/videos/${id}`),
    enabled: !!id,
  });
}

/**
 * Retry failed capture mutation
 */
export function useRetryCapture() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<VideoClip>(`/api/videos/${id}/retry`, {}),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: videoKeys.all });
      queryClient.invalidateQueries({ queryKey: videoKeys.detail(id) });
    },
  });
}

/**
 * Cancel stuck capture mutation
 */
export function useCancelCapture() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ message: string }>(`/api/videos/${id}/cancel`, {}),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: videoKeys.all });
      queryClient.invalidateQueries({ queryKey: videoKeys.detail(id) });
    },
  });
}

/**
 * Delete video clip mutation
 */
export function useDeleteVideo() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ message: string }>(`/api/videos/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: videoKeys.all });
    },
  });
}
